import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import type { Event, MeetpointConfig } from "@attendik/types/admin-schemas";

import { api } from "../../api/client.js";
import { isApiError } from "../../api/errors.js";
import { Button } from "../../components/ui/button.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../../components/ui/card.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Select } from "../../components/ui/select.js";
import { Badge } from "../../components/ui/badge.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../../components/states.js";
import { formatDateTime } from "../../lib/format.js";
import { syncStatusVariant } from "../shared/pills.js";

const PASSWORD_MASK = "••••••••";

/**
 * Integration tab — the showcase screen (build plan P2-T8d). Per-event
 * Meetpoint config, test-connection, a polling toggle + interval slider
 * (10–300s), and Sync-now with sync-run polling.
 */
export function IntegrationTab({ event }: { event: Event }): JSX.Element {
  const [config, setConfig] = useState<MeetpointConfig | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoadError(null);
    try {
      const cfg = await api.getMeetpointConfig(event.id);
      setConfig(cfg);
    } catch (err) {
      if (isApiError(err) && err.status === 404) {
        setConfig(null);
      } else {
        setLoadError(isApiError(err) ? err.message : "Failed to load configuration.");
      }
    }
  }, [event.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (config === undefined) {
    return <LoadingState />;
  }
  if (loadError) {
    return <ErrorState message={loadError} onRetry={() => void load()} />;
  }

  return (
    <div className="space-y-6">
      <ConnectionCard event={event} config={config} onSaved={load} />
      {config ? (
        <>
          <PollingCard event={event} config={config} onSaved={load} />
          <SyncCard event={event} hasConfig />
        </>
      ) : null}
    </div>
  );
}

function ConnectionCard({
  event,
  config,
  onSaved
}: {
  event: Event;
  config: MeetpointConfig | null;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = useState(config === null);
  const [baseUrl, setBaseUrl] = useState(
    config?.base_url ?? "https://platform.meetpoint.si"
  );
  const [username, setUsername] = useState(config?.username ?? "");
  // The password starts masked when a config exists — submitting the mask
  // retains the stored password server-side.
  const [password, setPassword] = useState(config ? PASSWORD_MASK : "");
  const [meetpointEventId, setMeetpointEventId] = useState(
    config?.meetpoint_event_id ?? ""
  );
  const [languageId, setLanguageId] = useState(config?.language_id ?? "EN");
  const [statusIds, setStatusIds] = useState(
    (config?.invitation_status_ids ?? [-1]).join(", ")
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const handleSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const parsedStatusIds = statusIds
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && (n === -1 || n > 0));
    if (parsedStatusIds.length === 0) {
      setError("At least one invitation status ID is required.");
      return;
    }
    setSaving(true);
    try {
      await api.putMeetpointConfig(event.id, {
        base_url: baseUrl.trim(),
        username: username.trim(),
        password,
        meetpoint_event_id: meetpointEventId.trim(),
        language_id: languageId.trim() || "EN",
        invitation_status_ids: parsedStatusIds,
        polling_enabled: config?.polling_enabled ?? false,
        polling_interval_seconds: config?.polling_interval_seconds ?? 30
      });
      setEditing(false);
      await onSaved();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not save the configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (): Promise<void> => {
    setTestResult(null);
    setTestError(null);
    setTesting(true);
    try {
      const result = await api.testMeetpointConnection(event.id);
      setTestResult(
        `Connected — ${result.participant_count} registered participant${
          result.participant_count === 1 ? "" : "s"
        }.`
      );
    } catch (err) {
      setTestError(
        isApiError(err)
          ? `Connection failed (${err.code}): ${err.message}`
          : "Connection failed."
      );
    } finally {
      setTesting(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    setError(null);
    try {
      await api.deleteMeetpointConfig(event.id);
      await onSaved();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not remove the configuration.");
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Meetpoint connection</CardTitle>
        {config && !editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button size="sm" variant="outline" onClick={handleRemove}>
              Remove
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="pt-0">
        {!editing && config ? (
          <div className="space-y-4">
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Field label="Base URL" value={config.base_url} />
              <Field label="Username" value={config.username} />
              <Field label="Password" value={PASSWORD_MASK} />
              <Field label="Meetpoint event ID" value={config.meetpoint_event_id} />
              <Field label="Language" value={config.language_id} />
              <Field
                label="Invitation status IDs"
                value={config.invitation_status_ids.join(", ")}
              />
            </dl>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? "Testing…" : "Test connection"}
              </Button>
              {testResult ? (
                <span className="text-sm text-emerald-700">{testResult}</span>
              ) : null}
              {testError ? (
                <span role="alert" className="text-sm text-destructive">
                  {testError}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSave}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="mp-base-url">Base URL</Label>
                <Input
                  id="mp-base-url"
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  required
                />
              </div>
              <ConnectEventDropdown
                eventId={event.id}
                baseUrl={baseUrl}
                username={username}
                password={password}
                languageId={languageId}
                selected={meetpointEventId}
                onSelect={(id) => setMeetpointEventId(id)}
              />
              <div className="space-y-1.5">
                <Label htmlFor="mp-username">Username</Label>
                <Input
                  id="mp-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mp-password">Password</Label>
                <Input
                  id="mp-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                {config ? (
                  <p className="text-xs text-muted-foreground">
                    Leave as {PASSWORD_MASK} to keep the stored password.
                  </p>
                ) : null}
              </div>
              {/* Language and invitation-status-ids are intentionally NOT
                  shown in the form — operators don't think in those terms.
                  Server defaults: language_id = "EN", invitation_status_ids
                  = [-1] (All). Both are still editable directly in
                  the DB if a customer ever needs something else. */}
              <div className="hidden">
              </div>
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              {config ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
              ) : null}
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : config ? "Save changes" : "Save configuration"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function PollingCard({
  event,
  config,
  onSaved
}: {
  event: Event;
  config: MeetpointConfig;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [enabled, setEnabled] = useState(config.polling_enabled);
  const [interval, setInterval] = useState(config.polling_interval_seconds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty =
    enabled !== config.polling_enabled ||
    interval !== config.polling_interval_seconds;

  const handleSave = async (): Promise<void> => {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      await api.putMeetpointConfig(event.id, {
        base_url: config.base_url,
        username: config.username,
        password: PASSWORD_MASK,
        meetpoint_event_id: config.meetpoint_event_id,
        language_id: config.language_id,
        invitation_status_ids: config.invitation_status_ids,
        polling_enabled: enabled,
        polling_interval_seconds: interval
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not update polling.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Polling</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Automatic polling</p>
            <p className="text-xs text-muted-foreground">
              Pulls registered participants on the interval below while the
              event is active.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Toggle automatic polling"
            onClick={() => setEnabled((v) => !v)}
            className={`relative h-6 w-11 rounded-full transition-colors ${
              enabled ? "bg-accent" : "bg-input"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-background transition-transform ${
                enabled ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="polling-interval">Polling interval</Label>
            <span className="text-sm font-medium tabular-nums">
              {interval}s
            </span>
          </div>
          <input
            id="polling-interval"
            type="range"
            min={10}
            max={300}
            step={5}
            value={interval}
            disabled={!enabled}
            onChange={(e) => setInterval(Number(e.target.value))}
            className="w-full accent-[hsl(var(--accent))] disabled:opacity-50"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>10s</span>
            <span>300s</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Last sync: {formatDateTime(config.last_synced_at)}</span>
          <span>
            {enabled
              ? `Next sync: roughly every ${interval}s`
              : "Polling disabled"}
          </span>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {saved && !dirty ? (
          <p className="text-sm text-emerald-700">Polling settings saved.</p>
        ) : null}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save polling settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SyncCard({
  event,
  hasConfig
}: {
  event: Event;
  hasConfig: boolean;
}): JSX.Element {
  const [runs, setRuns] = useState<Awaited<ReturnType<typeof api.listSyncRuns>>["data"]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadRuns = useCallback(async (): Promise<void> => {
    setLoadError(null);
    try {
      const page = await api.listSyncRuns(event.id, 1, 20);
      setRuns(page.data);
    } catch (err) {
      setLoadError(isApiError(err) ? err.message : "Failed to load sync runs.");
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => {
    void loadRuns();
    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
      }
    };
  }, [loadRuns]);

  const runSync = async (clean: boolean): Promise<void> => {
    if (
      clean &&
      !window.confirm(
        "Remove attendees that Meetpoint no longer returns?\n\n" +
          "This permanently deletes Meetpoint-sourced attendees that are missing " +
          "from the latest Meetpoint pull. Attendees who have been scanned, or who " +
          "were added manually, are never deleted."
      )
    ) {
      return;
    }
    setSyncMessage(null);
    setSyncError(null);
    setSyncing(true);
    try {
      const { sync_log_id, deleted_count } = await api.triggerSync(event.id, clean);
      // Poll the sync-run endpoint until it reaches a terminal state.
      for (let attempt = 0; attempt < 30; attempt += 1) {
        const run = await api.getSyncRun(sync_log_id);
        if (run.status !== "RUNNING") {
          setSyncMessage(
            run.status === "SUCCESS" || run.status === "PARTIAL"
              ? `Sync ${run.status.toLowerCase()} — ${run.inserted_count} inserted, ${run.updated_count} updated, ${run.skipped_count} skipped${clean ? `, ${deleted_count} removed` : ""}.`
              : `Sync failed: ${run.error ?? "unknown error"}.`
          );
          break;
        }
        await new Promise((resolve) => {
          pollTimer.current = setTimeout(resolve, 1000);
        });
      }
      await loadRuns();
    } catch (err) {
      if (isApiError(err) && err.code === "SYNC_IN_PROGRESS") {
        setSyncError("A sync is already running for this event.");
      } else {
        setSyncError(isApiError(err) ? err.message : "Sync failed.");
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Recent sync runs</CardTitle>
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={() => void runSync(true)}
            disabled={syncing || !hasConfig}
            title="Pull from Meetpoint, then delete Meetpoint attendees no longer returned (never those with scans or added manually)."
          >
            {syncing ? "Working…" : "Clean sync"}
          </Button>
          <Button onClick={() => void runSync(false)} disabled={syncing || !hasConfig}>
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {syncMessage ? (
          <p className="mb-3 text-sm text-emerald-700">{syncMessage}</p>
        ) : null}
        {syncError ? (
          <p role="alert" className="mb-3 text-sm text-destructive">
            {syncError}
          </p>
        ) : null}

        {loading ? (
          <LoadingState />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={() => void loadRuns()} />
        ) : runs.length === 0 ? (
          <EmptyState
            title="No sync runs yet"
            description="Trigger a sync to pull participants from Meetpoint."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Finished</TableHead>
                <TableHead>Inserted</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Skipped</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>{run.mode}</TableCell>
                  <TableCell>
                    <Badge variant={syncStatusVariant(run.status)}>
                      {run.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(run.started_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(run.finished_at)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {run.inserted_count}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {run.updated_count}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {run.skipped_count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 break-all">{value}</dd>
    </div>
  );
}

interface ConnectEvent {
  event_id: string;
  name: string;
  start_at: string | null;
  end_at: string | null;
}

/**
 * Dropdown that replaces the freeform "Meetpoint event ID" input. Click
 * "Load events from Meetpoint" → server-side POST to /meetpoint/connect
 * with the typed credentials → the response populates the `<Select>` so
 * the admin user picks the right event instead of typing a numeric id.
 *
 * If a saved value already exists (`selected !== ""`) it stays as the
 * currently-selected option until the user successfully connects, so
 * we don't drop the existing config while loading.
 */
function ConnectEventDropdown({
  eventId,
  baseUrl,
  username,
  password,
  languageId,
  selected,
  onSelect
}: {
  eventId: string;
  baseUrl: string;
  username: string;
  password: string;
  languageId: string;
  selected: string;
  onSelect: (id: string) => void;
}): JSX.Element {
  const [connecting, setConnecting] = useState(false);
  const [events, setEvents] = useState<ConnectEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = async (): Promise<void> => {
    setError(null);
    if (!username.trim() || !password.trim()) {
      setError("Enter a username + password to load events.");
      return;
    }
    // The password may be the mask placeholder (••••••••) when an existing
    // config is being edited. The server detects that and uses the stored
    // encrypted password — no fresh re-entry required.
    setConnecting(true);
    try {
      const res = await api.connectMeetpoint(eventId, {
        base_url: baseUrl,
        username: username.trim(),
        password,
        language_id: languageId || "EN"
      });
      setEvents(res.events);
      // Auto-select if MP returned exactly one event — saves a click.
      if (res.events.length === 1 && !selected) {
        onSelect(res.events[0]!.event_id);
      }
    } catch (err) {
      const code = isApiError(err) ? err.code : "ERROR";
      const msg = err instanceof Error ? err.message : "Load failed";
      setError(`${code}: ${msg}`);
    } finally {
      setConnecting(false);
    }
  };

  // Build the options. When we haven't loaded events yet but a saved
  // value is set, render a single option for that value so the Select
  // shows the persisted choice without a forced load.
  const hasLoaded = events !== null;
  const options = hasLoaded
    ? events!.map((ev) => ({
        value: ev.event_id,
        label: ev.start_at
          ? `${ev.name} (#${ev.event_id} · ${formatDateTime(ev.start_at)})`
          : `${ev.name} (#${ev.event_id})`
      }))
    : selected
      ? [{ value: selected, label: `#${selected} (saved value)` }]
      : [];

  return (
    <div className="space-y-1.5">
      <Label htmlFor="mp-event-id">Meetpoint event</Label>
      <Select
        id="mp-event-id"
        value={selected}
        onChange={(e) => onSelect(e.target.value)}
        required
      >
        <option value="" disabled>
          {hasLoaded
            ? events!.length === 0
              ? "— no events visible —"
              : "— pick an event —"
            : "— load events to pick —"}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleLoad}
          disabled={connecting}
        >
          {connecting
            ? "Loading…"
            : hasLoaded
              ? "Refresh from Meetpoint"
              : "Load events from Meetpoint"}
        </Button>
        {hasLoaded ? (
          <span className="text-xs text-muted-foreground">
            {events!.length} event{events!.length === 1 ? "" : "s"} visible
          </span>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
